import * as z from 'zod';

export const AzureSentinelStandardTableSchema = z.object({
  uuid: z.string().nonempty(),
  table: z.string().min(1, 'Table name is required')
});

export type AzureSentinelStandardTable = z.infer<typeof AzureSentinelStandardTableSchema>;

export const AzureSentinelCustomTableSchema = z.object({
  uuid: z.string().optional(),
  table: z.string().min(1, 'Table name is required').regex(/^\S+$/, 'Table name cannot contain spaces'),
  source_type_field: z.string(),
  table_search_criteria: z.string(),
  mac_fields: z.array(z.string().nonempty()).optional().default([]),
  ipv4_fields: z.array(z.string().nonempty()).optional().default([]),
  hostname_fields: z.array(z.string().nonempty()).optional().default([]),
  time_fields: z.array(z.string().nonempty()).optional().default([])
});

export type AzureSentinelCustomTable = z.infer<typeof AzureSentinelCustomTableSchema>;

export const getAzureSentinelStandardTableName = (table: AzureSentinelStandardTable | string) =>
  typeof table === 'string' ? table : table.table;

export const getAzureSentinelStandardTableUuid = (table: AzureSentinelStandardTable | string) =>
  typeof table === 'string' ? table : table.uuid;

export const getAzureSentinelCustomTableUuid = (table: AzureSentinelCustomTable) => table.uuid ?? table.table;

export const normalizeAzureSentinelStandardTables = (
  tables?: (AzureSentinelStandardTable | string)[]
): AzureSentinelStandardTable[] =>
  (tables ?? []).map(table =>
    typeof table === 'string'
      ? {
          uuid: table,
          table
        }
      : table
  );

export const sentinelPartition = z.object({
  custom_tables: z.array(AzureSentinelCustomTableSchema).optional(),
  standard_tables: z.array(AzureSentinelStandardTableSchema).optional(),
  workspace_name: z.string().max(50).optional(),
  subscription_id: z.string().max(50).optional(),
  resource_group_name: z.string().max(50).optional(),
  page_size: z.coerce.number().min(1).max(2000),
  display_id: z.boolean(),
  verify_ssl: z.boolean(),
  auth_method: z.enum(['secret', 'certificate_enc_private_key', 'certificate']),
  store_event_raw: z.boolean(),
  use_asset_filtering: z.boolean().optional(),
  store_table_in_class: z.boolean(),
  client_id: z.string().max(50).optional(),
  tenant_id: z.string().max(50).optional(),
  certificate: z.string().max(50).optional(),
  resource_url: z.string().url().max(150).or(z.literal('')),
  workspace_id: z.string().max(50).optional(),
  client_secret: z.string().max(50).optional(),
  certificate_password: z.string().max(50).optional()
});

export const sentinelAttackIqPartition = z.object({
  use_security_events: z.enum(['custom_rules', 'all', 'security_events'])
});

const mode = z.enum(['Automatic', 'Ad Hoc']);

export type SentinelMode = z.infer<typeof mode>;

export const sentinelGeneralPartition = z.object({
  display_name: z.string().max(50).nonempty('This field is required'),

  mode
});

export const sentinelConfigFormSchema = z.object({
  general: sentinelGeneralPartition.required(),
  azure_sentinel: sentinelPartition,
  attack_iq: sentinelAttackIqPartition.required()
});

export type SentinelConfig = z.infer<typeof sentinelConfigFormSchema>;
